import React, { useRef } from 'react';
import { IconBrandInstagram, IconBrandWhatsapp, IconLink, IconUsers } from '@tabler/icons-react';
import { Anchor, Glow, Pill, Core, Wires, Beam, Sparks, gsap, travel, flare, blink, charge, ambience, entrance, useSceneAnimation } from '../../../../../../components/Scene';
import './styles.scss';

// New people arrive from each channel, pass through the hub under a spotlight
// and land in the list, where each one is grouped by interest.
const SOURCES = [
  { id: 'instagram', x: 70, icon: IconBrandInstagram },
  { id: 'whatsapp', x: 180, icon: IconBrandWhatsapp },
  { id: 'link', x: 290, icon: IconLink },
];
const WIRES = [
  { id: 'instagram', tone: 'warm', d: 'M70 83 C70 118 168 108 168 142', joints: [[70, 83], [168, 142]] },
  { id: 'whatsapp', tone: 'warm', d: 'M180 83 V142', joints: [[180, 83], [180, 142]] },
  { id: 'link', tone: 'warm', d: 'M290 83 C290 118 192 108 192 142', joints: [[290, 83], [192, 142]] },
  { id: 'list', tone: 'cool', d: 'M180 218 V250', joints: [[180, 218], [180, 250]] },
];
const CONTACTS = [
  { name: 'Ana Souza', initials: 'AS', interest: 'interestFitness', tone: 'cool' },
  { name: 'Bruno Lima', initials: 'BL', interest: 'interestWellness', tone: 'violet' },
  { name: 'Carla Dias', initials: 'CD', interest: 'interestSports', tone: 'warm' },
];
const SPARKS = [[170, 44], [190, 66], [176, 98], [196, 118], [164, 126]];
const BASE = 126;
const STEP = 2.2;

function build(q, { still }) {
  const count = q('[data-count]')[0];
  const badge = q('[data-badge]');
  if (still) return;
  ambience(q);
  const intro = entrance(q);
  const rows = q('[data-row]');
  const tags = q('[data-tag]');
  const reset = () => {
    gsap.set(rows, { opacity: 0.45 });
    gsap.set(tags, { opacity: 0, scale: 0.6 });
    gsap.set(badge, { opacity: 0 });
    count.textContent = String(BASE);
  };
  reset();
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.3, delay: intro.duration() - 0.2, onRepeat: reset });
  SOURCES.forEach(({ id }, i) => {
    const at = i * STEP;
    flare(loop, q, id, at);
    blink(loop, q, [`${id}-0`], at + 0.1);
    travel(loop, q, id, at + 0.12, 0.65);
    blink(loop, q, [`${id}-1`], at + 0.75);
    charge(loop, q, at + 0.78);
    loop.fromTo(badge, { opacity: 0, y: 6, scale: 0.8 }, { opacity: 1, y: 0, scale: 1, duration: 0.3, ease: 'back.out(2)' }, at + 0.8)
      .to(badge, { opacity: 0, y: -8, duration: 0.4, ease: 'power2.in' }, at + 1.5)
      .call(() => { count.textContent = String(BASE + i + 1); }, null, at + 0.82);
    blink(loop, q, ['list-0'], at + 0.9);
    travel(loop, q, 'list', at + 0.92, 0.45);
    blink(loop, q, ['list-1'], at + 1.35);
    loop.to(rows[i], { opacity: 1, duration: 0.3 }, at + 1.38)
      .fromTo(q(`[data-row-glow="${i}"]`), { xPercent: -100, opacity: 1 }, { xPercent: 100, opacity: 0, duration: 0.9, ease: 'power2.out' }, at + 1.38)
      .fromTo(tags[i], { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2.2)' }, at + 1.5);
  });
  loop.to([rows, tags], { opacity: (n, el) => (el.hasAttribute('data-row') ? 0.45 : 0), duration: 0.5 }, `+=1.2`);
}

export default function LeadsScene({ t }) {
  const stage = useRef(null);
  useSceneAnimation(stage, build);
  return <div className="studio-feature-art__stage studio-feature-art__stage--leads" ref={stage}>
    <Glow x={180} y={180} tone="cool" size={210} />
    <Glow x={64} y={92} tone="warm" size={130} />
    <Beam x={180} height={184} />
    <Sparks points={SPARKS} />
    <Wires wires={WIRES} />
    {SOURCES.map(({ id, x, icon }) => <Anchor key={id} x={x} y={70}>
      <Pill id={id} tone="warm" icon={icon}>{id === 'link' ? t('artLinkInBio') : id === 'instagram' ? 'Instagram' : 'WhatsApp'}</Pill>
    </Anchor>)}
    <Anchor x={180} y={180}><Core icon={IconUsers} /></Anchor>
    <Anchor x={222} y={146}><span className="studio-feature-art__badge is-cool" data-badge>+1</span></Anchor>
    <Anchor x={180} y={318}>
      <div className="studio-feature-art__card studio-feature-art__contacts" data-enter>
        <div className="studio-feature-art__contacts-head"><IconUsers aria-hidden="true" /><span>{t('leads')}</span><b data-count>{BASE + CONTACTS.length}</b></div>
        {CONTACTS.map((contact, i) => <div key={contact.initials} className="studio-feature-art__contact" data-row>
          <span className="studio-feature-art__contact-glow" data-row-glow={i} />
          <span className={`studio-feature-art__avatar is-${contact.tone}`}>{contact.initials}</span>
          <span className="studio-feature-art__contact-name">{contact.name}</span>
          <span className={`studio-feature-art__tag is-${contact.tone}`} data-tag>{t(contact.interest)}</span>
        </div>)}
      </div>
    </Anchor>
  </div>;
}
